import React, { useState } from 'react';
import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  Switch,
  Text,
  Divider,
  useColorModeValue,
  SimpleGrid,
  Card,
  CardBody,
  Heading,
  Icon,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import {
  FiMail,
  FiBell,
  FiCreditCard,
  FiAlertCircle,
  FiRefreshCw,
  FiShield,
} from 'react-icons/fi';
import {
  getNotifications,
  markNotificationAsRead,
  deleteNotification,
  markAllNotificationsAsRead,
} from '../services/api';

const NotificationSettings = () => {
  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    planRenewalAlerts: true,
    usageAlerts: true,
    systemUpdates: true,
    adminNotifications: true,
  });

  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const handleToggle = (setting) => {
    setSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
    
    toast({
      title: "Setting updated",
      description: `${setting.replace(/([A-Z])/g, ' $1').trim()} has been ${settings[setting] ? 'disabled' : 'enabled'}`,
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const notificationGroups = [
    {
      title: "Delivery Methods",
      settings: [
        {
          id: "email-notifications",
          label: "Email Notifications",
          icon: FiMail,
          setting: "emailNotifications",
          description: "Receive notifications via email"
        },
        {
          id: "push-notifications",
          label: "Push Notifications",
          icon: FiBell,
          setting: "pushNotifications",
          description: "Receive push notifications in your browser"
        }
      ]
    },
    {
      title: "Notification Types",
      settings: [
        {
          id: "plan-renewal",
          label: "Plan Renewal Alerts",
          icon: FiCreditCard,
          setting: "planRenewalAlerts",
          description: "Get notified about subscription renewals"
        },
        {
          id: "usage-alerts",
          label: "Usage Alerts",
          icon: FiAlertCircle,
          setting: "usageAlerts",
          description: "Receive alerts about usage limits"
        },
        {
          id: "system-updates",
          label: "System Updates",
          icon: FiRefreshCw,
          setting: "systemUpdates",
          description: "Stay informed about system updates"
        },
        {
          id: "admin-notifications",
          label: "Admin Notifications",
          icon: FiShield,
          setting: "adminNotifications",
          description: "Receive administrative notifications"
        }
      ]
    }
  ];

  return (
    <Box p={{ base: 4, md: 6 }} maxW="1200px" mx="auto">
      <VStack spacing={6} align="stretch">
        <Heading size="lg" mb={4}>Notification Settings</Heading>
        
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          {notificationGroups.map((group) => (
            <Card key={group.title} bg={bgColor} borderWidth="1px" borderColor={borderColor}>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <Heading size="md" mb={2}>{group.title}</Heading>
                  <Divider />
                  {group.settings.map((item) => (
                    <Tooltip key={item.id} label={item.description} placement="top">
                      <FormControl 
                        display="flex" 
                        alignItems="center"
                        p={3}
                        borderRadius="md"
                        _hover={{ bg: hoverBg }}
                        transition="all 0.2s"
                      >
                        <Icon as={item.icon} boxSize={5} mr={3} color="blue.500" />
                        <FormLabel htmlFor={item.id} mb="0" flex="1">
                          {item.label}
                        </FormLabel>
                        <Switch
                          id={item.id}
                          isChecked={settings[item.setting]}
                          onChange={() => handleToggle(item.setting)}
                          colorScheme="blue"
                        />
                      </FormControl>
                    </Tooltip>
                  ))}
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      </VStack>
    </Box>
  );
};

export default NotificationSettings; 